/**
 * Helper to create jobs from wizard configuration
 */
import type { FeatureCollection, Feature } from "geojson";
import { GeoData } from "@rogoag/shared-ts";
import type { User } from "../../../../../../../models";
import { Job, type Company, type Deal, type Contact, type Branch, type Grower } from "../../../../../../../models";
import type { GeodataGroup, GeoType, WizardConfigurationState } from "../../WizardContainer";
import type { PropertyMappings } from "../../../../../shared/PropertyMappings.types";
import { FileOptionTypes, GeoJSONOption } from "../../../../../../../utilities/types";
import { groupByFilePath } from "../../../BulkImportForm.helpers";
import { extractPropertyValue, mergePropertyMappings } from "../../../../../shared/extractJobFieldsFromBoundary";
import { normalizePointsFC } from "../../../../../../../domain/sample/sampleService";
import { disambiguateDirectionalFieldNames } from "./createJobsFromWizard.helpers";

/**
 * Generate a single file name with the given suffix (lazy generation)
 * Optimized: only generates the specific name needed instead of all 5 at once
 */
export function generateFileName(growerName: string, farmName: string, fieldName: string, suffix: string): string {
	const prefix = [fieldName, farmName, growerName].filter((val) => val && val !== "").join("_");
	const randomId = Math.random().toString(36).substring(2, 6);
	return `${prefix}_${randomId}_${suffix}`;
}

/**
 * Extract a property value from a match result (boundary → zones → centroids → cores priority)
 */
function extractPropertyValueFromMatch(
	match: {
		boundary?: FeatureCollection;
		zones?: FeatureCollection;
		centroids?: FeatureCollection;
		cores?: FeatureCollection;
	},
	propertyNames: string[] | null | undefined,
): string {
	if (!propertyNames || propertyNames.length === 0) return "";

	// Try boundary first
	if (match.boundary?.features?.[0]) {
		const value = extractPropertyValue(match.boundary.features[0], propertyNames);
		if (value) return value;
	}
	// Then zones
	if (match.zones?.features?.[0]) {
		const value = extractPropertyValue(match.zones.features[0], propertyNames);
		if (value) return value;
	}
	// Then centroids
	if (match.centroids?.features?.[0]) {
		const value = extractPropertyValue(match.centroids.features[0], propertyNames);
		if (value) return value;
	}
	// Finally cores
	if (match.cores?.features?.[0]) {
		const value = extractPropertyValue(match.cores.features[0], propertyNames);
		if (value) return value;
	}
	return "";
}

/**
 * Remove internal tags from GeoJSON features
 */
function removeInternalTags(geoJSON: FeatureCollection | undefined): FeatureCollection | undefined {
	if (!geoJSON) return undefined;
	return {
		...geoJSON,
		features: geoJSON.features.map((f) => {
			const props = { ...f.properties };
			delete props.__groupKey;
			delete props.__uniqueJobKey;
			return { ...f, properties: props };
		}),
	};
}

export interface CreateJobsParams {
	geodataGroups: GeodataGroup[];
	geoTypeSelections: Record<string, GeoType | null>;
	excludedGroupIds: Set<string>;
	propertyMappings: Record<string, PropertyMappings>;
	configuration: WizardConfigurationState;
	company?: Company | null;
	allDeals?: Deal[];
	salespersons?: Contact[];
	branches?: Branch[];
	/** Pre-selected branch from parent form (applied to created jobs) */
	selectedBranch?: Branch;
	growers?: Grower[];
	user?: User;
}

/**
 * Create jobs from wizard configuration by matching boundaries with zones/centroids/cores
 */
export async function createJobsFromWizard(params: CreateJobsParams): Promise<Job[]> {
	const { geodataGroups, geoTypeSelections, excludedGroupIds, propertyMappings, selectedBranch, user } = params;

	// Filter out excluded groups
	const activeGroups = geodataGroups.filter((g) => !excludedGroupIds.has(g.id));

	// Separate groups by their geo-type
	const boundaryGeoJSONs: FeatureCollection[] = [];
	const zonesGeoJSONs: FeatureCollection[] = [];
	const centroidsGeoJSONs: FeatureCollection[] = [];
	const coresGeoJSONs: FeatureCollection[] = [];

	// Group mappings by geo-type for later use
	const groupMappingsByGeoType = new Map<string, { group: GeodataGroup; mappings: PropertyMappings | undefined }>();

	for (const group of activeGroups) {
		const geoType = geoTypeSelections[group.id];
		if (!geoType) continue;

		// Shallow clone and tag features with group ID for tracking (optimized: ~9000x faster than JSON.parse/stringify)
		const taggedGeoJSON: FeatureCollection = {
			...group.geoJSON,
			features: group.geoJSON.features.map((f) => ({
				...f,
				properties: { ...f.properties, __groupKey: group.id },
			})),
		};

		const groupMapping = propertyMappings[group.id];
		groupMappingsByGeoType.set(group.id, { group, mappings: groupMapping });

		switch (geoType) {
			case "Boundary": {
				// Merge boundary polygons into MultiPolygon for better matching
				const merged = GeoData.mergeBoundaryPolygonsIntoMultiPolygon(taggedGeoJSON);
				if (merged) {
					// Re-add tags to merged features
					merged.features.forEach((feature) => {
						if (!feature.properties) feature.properties = {};
						feature.properties.__groupKey = group.id;
					});
					boundaryGeoJSONs.push(merged);
				}
				break;
			}
			case "Zones":
				zonesGeoJSONs.push(taggedGeoJSON);
				break;
			case "Centroids":
				centroidsGeoJSONs.push(taggedGeoJSON);
				break;
			case "Cores":
				coresGeoJSONs.push(taggedGeoJSON);
				break;
		}
	}

	if (boundaryGeoJSONs.length === 0) {
		throw new Error("No boundary groups found. At least one boundary is required.");
	}

	// Match boundaries with other features using spatial matching
	const matchResults = await GeoData.match(boundaryGeoJSONs, zonesGeoJSONs, centroidsGeoJSONs, coresGeoJSONs);

	if (matchResults.matches.length === 0) {
		throw new Error("Unable to match geodata. Please ensure boundaries are correctly assigned.");
	}

	// Create jobs from matches
	const jobs: Job[] = [];

	for (const match of matchResults.matches) {
		if (!match.boundary) continue;

		// Collect property mappings from ALL matched groups (boundary, zones, centroids, cores)
		// so that properties like EventID that only exist in centroid files are still mapped.
		const matchedGroupMappings: Record<string, PropertyMappings> = {};
		for (const collection of [match.boundary, match.zones, match.centroids, match.cores]) {
			const key = collection?.features[0]?.properties?.__groupKey;
			if (key && !matchedGroupMappings[key]) {
				const m = groupMappingsByGeoType.get(key)?.mappings;
				if (m) matchedGroupMappings[key] = m;
			}
		}
		const mappings =
			Object.keys(matchedGroupMappings).length > 0 ? mergePropertyMappings(matchedGroupMappings) : undefined;

		// Extract property values using mappings
		const fieldName = extractPropertyValueFromMatch(match, mappings?.field);
		const farmName = extractPropertyValueFromMatch(match, mappings?.farm);
		const growerName = extractPropertyValueFromMatch(match, mappings?.grower);
		const fieldId = extractPropertyValueFromMatch(match, mappings?.fieldId);
		const labName = extractPropertyValueFromMatch(match, mappings?.labName);
		const labInstructions = extractPropertyValueFromMatch(match, mappings?.labInstructions);
		const submissionNotes = extractPropertyValueFromMatch(match, mappings?.submissionNotes);
		const yieldGoal = extractPropertyValueFromMatch(match, mappings?.yieldGoal);
		const submitterName = extractPropertyValueFromMatch(match, mappings?.submitterName);
		const submitterEmail = extractPropertyValueFromMatch(match, mappings?.submitterEmail);
		const growerPhoneNumber = extractPropertyValueFromMatch(match, mappings?.growerPhoneNumber);
		const eventId = extractPropertyValueFromMatch(match, mappings?.eventId);
		const labSubmissionCode = extractPropertyValueFromMatch(match, mappings?.labSubmissionCode);
		const externalId = extractPropertyValueFromMatch(match, mappings?.externalId);
		const externalFarmId = extractPropertyValueFromMatch(match, mappings?.externalFarmId);
		const externalLabId = extractPropertyValueFromMatch(match, mappings?.externalLabId);

		// Create job with defaults (deals assigned in deal-configuration step)
		const job: Job = Job.defaultForImport(
			fieldName,
			0, // depth — set by deal assignment
			0, // density — set by deal assignment
			growerName,
			0, // frequencyOfMicros — set by deal assignment
			"", // testPackage — set by deal assignment
			farmName,
		);

		job.jobFlags = ["Bulk Upload"];
		job.dealIds = [];
		job.branchIds = selectedBranch ? [selectedBranch.id] : [];
		job.fieldId = fieldId;
		job.submitterEmail = submitterEmail || user?.email || "";
		job.submitterName = submitterName || user?.name || "";
		if (growerPhoneNumber) job.growerPhoneNumber = growerPhoneNumber;
		if (labName) job.importLabName = labName;
		if (labInstructions) job.labInstructions = labInstructions;
		if (submissionNotes) job.submissionNotes = submissionNotes;
		if (yieldGoal) job.yieldGoal = yieldGoal;
		if (eventId) job.eventId = eventId;
		if (labSubmissionCode) job.labSubmissionCode = labSubmissionCode;
		if (externalId) job.externalId = externalId;
		if (externalFarmId) job.externalFarmId = externalFarmId;
		if (externalLabId) job.externalLabId = externalLabId;

		// Clean GeoJSONs (remove internal tags)
		const cleanBoundary = removeInternalTags(match.boundary)!;
		const cleanZones = removeInternalTags(match.zones);
		const cleanCentroids = removeInternalTags(match.centroids);
		const cleanCores = removeInternalTags(match.cores);

		// Merge centroids and cores into points
		let cleanPoints: FeatureCollection | undefined = undefined;
		if (cleanCentroids && cleanCores) {
			cleanPoints = GeoData.merge([cleanCentroids, cleanCores]);
		} else {
			cleanPoints = cleanCentroids || cleanCores;
		}

		// Generate file names lazily - only create names for files that actually exist
		// Optimized: generates 1-4 names instead of always generating all 5
		const bndName = generateFileName(growerName, farmName, fieldName, "boundary");
		const bndOption: GeoJSONOption = { name: bndName, geoJSON: cleanBoundary };

		const znsOptions: GeoJSONOption[] = cleanZones
			? [{ name: generateFileName(growerName, farmName, fieldName, "zones"), geoJSON: cleanZones }]
			: [];
		const cdsOptions: GeoJSONOption[] = cleanCentroids
			? [{ name: generateFileName(growerName, farmName, fieldName, "centroids"), geoJSON: cleanCentroids }]
			: [];
		const crsOptions: GeoJSONOption[] = cleanCores
			? [{ name: generateFileName(growerName, farmName, fieldName, "cores"), geoJSON: cleanCores }]
			: [];

		job.fileOptions = {
			boundaryOptions: [{ type: FileOptionTypes.Boundary, options: [bndOption] }],
			pointsOptions: [
				{ type: FileOptionTypes.Centroids, options: cdsOptions },
				{ type: FileOptionTypes.Cores, options: crsOptions },
			],
			zonesOptions: [{ type: FileOptionTypes.Zones, options: znsOptions }],
			boundaryGeoJSON: cleanBoundary,
			centroidsGeoJSON: cleanCentroids,
			coresGeoJSON: cleanCores,
			zonesGeoJSON: cleanZones,
		};

		job.files = {
			...job.files,
			boundaryGeoJSON: cleanBoundary,
			pointsGeoJSON: cleanPoints ? normalizePointsFC(cleanPoints) : undefined,
			zonesGeoJSON: cleanZones,
		};

		jobs.push(job);
	}

	// Disambiguate duplicate field names with directional suffixes (N, S, E, W, etc.)
	disambiguateDirectionalFieldNames(jobs);

	// Create placeholder jobs for unmatched features
	const unmatchedJobs: Job[] = [];

	// Group all unmatched point features (centroids + cores) by file path in a single pass
	// Optimized: combines 2 groupByFilePath calls + Set creation into one iteration
	const unmatchedPointsByFile = new Map<string, Feature[]>();
	for (const feature of matchResults.unmatched.centroids) {
		const filePath = feature?.properties?.FilePath || "Unknown";
		if (!unmatchedPointsByFile.has(filePath)) {
			unmatchedPointsByFile.set(filePath, []);
		}
		unmatchedPointsByFile.get(filePath)!.push(feature);
	}
	for (const feature of matchResults.unmatched.cores) {
		const filePath = feature?.properties?.FilePath || "Unknown";
		if (!unmatchedPointsByFile.has(filePath)) {
			unmatchedPointsByFile.set(filePath, []);
		}
		unmatchedPointsByFile.get(filePath)!.push(feature);
	}

	for (const [filePath, allPointFeatures] of unmatchedPointsByFile) {
		if (allPointFeatures.length > 0) {
			// Clean internal tags using shallow clone (optimized: ~9x faster than JSON.parse/stringify)
			const pointsGeoJSON: FeatureCollection = {
				type: "FeatureCollection",
				features: allPointFeatures.map((f) => {
					const { __groupKey, __uniqueJobKey, ...restProps } = f.properties || {};
					return { ...f, properties: restProps };
				}),
			};

			const pointsOption: GeoJSONOption = { name: filePath, geoJSON: pointsGeoJSON };
			const fileName = filePath.split("/").pop() || filePath;

			const unmatchedJob = Job.defaultForImport(`(!) ${fileName} (unmatched points)`);
			unmatchedJob.jobFlags = ["Bulk Upload"];
			unmatchedJob.dealIds = [];
			unmatchedJob.branchIds = selectedBranch ? [selectedBranch.id] : [];
			unmatchedJob.submitterEmail = user?.email || "";
			unmatchedJob.submitterName = user?.name || "";
			unmatchedJob.files = {
				...unmatchedJob.files,
				pointsGeoJSON: pointsGeoJSON,
			};
			unmatchedJob.fileOptions = {
				...unmatchedJob.fileOptions,
				pointsOptions: [{ type: FileOptionTypes.Centroids, options: [pointsOption] }],
			};
			unmatchedJobs.push(unmatchedJob);
		}
	}

	// Group unmatched zones by file path for "UNMATCHED ZONES" jobs
	const unmatchedZonesByFile = groupByFilePath(matchResults.unmatched.zones);

	for (const [filePath, zoneFeatures] of unmatchedZonesByFile) {
		if (zoneFeatures.length > 0) {
			// Clean internal tags using shallow clone (optimized: ~9x faster than JSON.parse/stringify)
			const zonesGeoJSON: FeatureCollection = {
				type: "FeatureCollection",
				features: zoneFeatures.map((f) => {
					const { __groupKey, __uniqueJobKey, ...restProps } = f.properties || {};
					return { ...f, properties: restProps };
				}),
			};

			const zonesOption: GeoJSONOption = { name: filePath, geoJSON: zonesGeoJSON };
			const fileName = filePath.split("/").pop() || filePath;

			const unmatchedJob = Job.defaultForImport(`(!) ${fileName} (unmatched zones)`);
			unmatchedJob.jobFlags = ["Bulk Upload"];
			unmatchedJob.dealIds = [];
			unmatchedJob.branchIds = selectedBranch ? [selectedBranch.id] : [];
			unmatchedJob.submitterEmail = user?.email || "";
			unmatchedJob.submitterName = user?.name || "";
			unmatchedJob.files = {
				...unmatchedJob.files,
				zonesGeoJSON: zonesGeoJSON,
			};
			unmatchedJob.fileOptions = {
				...unmatchedJob.fileOptions,
				zonesOptions: [{ type: FileOptionTypes.Zones, options: [zonesOption] }],
			};
			unmatchedJobs.push(unmatchedJob);
		}
	}

	// Combine matched and unmatched jobs (unmatched FIRST so they appear at top of table)
	// Optimized: concat() is faster than spread for combining arrays
	const allJobs = unmatchedJobs.concat(jobs);

	return allJobs;
}
